// Email preparation service — runs entirely locally via Supabase client.
// No external API calls. Loads template config, fetches data, renders Handlebars.
// Uses the same helpers as the edge function — imported from shared, not duplicated.

import Handlebars from 'handlebars';
import type { SupabaseClient } from '../base.ts';
import type { TransactionFile } from '@shared/types/transaction.ts';
import type { GoogleEdgeFnClient, SentEmail } from '../edgeFn/GoogleEdgeFnClient.ts';
import { keysToCamelCase, keysToSnakeCase } from './flattenData.ts';
import { resolvePayerCarriers, type PayerQueryClient } from './carrierPayer.ts';
import {
  formatDate, formatTime, formatWeight, formatNumber, formatPhone, formatPrice,
  formatDriverDetails, getDayLocative, getDayName, productName,
  EMAIL_FOOTER, EMAIL_HEADER, EMAIL_FOOTER_WRAPPER,
  filterFiles, selectApprovedFile, getContactEmails,
  enrichCompany, enrichCarrier, resolveTransit,
  nationalCode, formatTransportMeans,
} from './helpers.ts';
import type {
  CompanyLike, CountryLike, FileLike, PriceLike, ProductLike, TransportLike, UserLike,
} from './helpers.ts';
import { BATCH_HELPERS } from './batchHelpers.ts';
import {
  assembleBatchScope, batchLanguagePolicy, batchRecipientsPolicy, dedupeAttachments,
  groupTransactionsByRecipients, isBatchScopePath, languageFor, pickBatchLanguage,
  resolveRecipientsFor, selectAttachmentsFor, BATCH_LANGUAGE_CONFLICT, MAX_BATCH_TRANSACTIONS,
  type BatchTxEntry,
} from './batchScope.ts';
import { classifyBySchema, extractAllEmailPlaceholders, isFieldPopulated } from '../template/validation.ts';
import { assignLogisticianOnTransportOrder } from '../transaction/assignLogisticianOnTransportOrder.ts';
import { EmployeeDao } from '../employee/EmployeeDao.ts';
import { TransactionDao } from '../transaction/TransactionDao.ts';

// ── Types ───────────────────────────────────────────────────────────────

interface DataSource {
  table: string;
  select: string;
  key?: string;
  filter?: { field: string; param: string };
}

interface EmailTemplateConfig {
  id: number;
  name: string;
  doc_document_type: string | null;
  email_body_template: string | null;
  email_body_templates: Record<string, string> | null;
  email_subject_template: string | null;
  email_subject_templates: Record<string, string> | null;
  email_language_source: string | null;
  email_recipients_config: { company: string; purpose: string } | null;
  email_recipients_static: string[] | null;
  email_cc: string[] | null;
  email_attachments_config: Array<{ document_type: string; select: 'filter' | 'approved' | 'generated' }> | null;
  data_sources: DataSource[];
  resolvers: string | null;
  placeholder_schema: import('@shared/types/common.ts').PlaceholderSchema | null;
  is_disabled: boolean;
  /** 'transaction' (default) | 'batch'. Rows predating the column read as null. */
  scope?: string | null;
  /** 'union' | 'split' — how differing recipient sets across a batch aggregate. */
  email_batch_recipients_policy?: string | null;
  /** 'block' | 'majority' | 'first' — how differing languages across a batch resolve. */
  email_batch_language_policy?: string | null;
}

interface PipelineParams {
  transactionId: number;
  userId?: string;
  userEmail?: string;
  transportId?: number;
}

export interface PreparedEmail {
  subject: string;
  html_body: string;
  message_html: string;
  footer_html: string;
  plain_text: string;
  recipients: string[];
  cc: string[];
  attachments: TransactionFile[];
  email_type: string;
  /** All missing placeholder paths (raw, unclassified). */
  validation_issues: string[];
  /** Classified using the template's placeholder_schema. Recipients always critical. */
  classified_issues: import('../template/validation.ts').ClassifiedIssues;
  placeholder_values: Record<string, unknown>;
}

// ── Batch result types ──────────────────────────────────────────────────

/** Attachments a single transaction contributed to a batch email. */
export interface BatchAttachmentGroup {
  transaction_id: number;
  reference_number: string;
  attachments: TransactionFile[];
}

/** Per-transaction validation breakdown so the operator sees WHICH row blocks. */
export interface BatchTransactionIssues {
  transaction_id: number;
  reference_number: string;
  critical: string[];
  info: string[];
}

/**
 * ONE outgoing email of a batch. Extends `PreparedEmail` additively, so every
 * existing consumer (PrepareEmailForm above all) renders a group unchanged.
 * With the `union` recipients policy a batch has exactly one group; with
 * `split` there is one per distinct recipient set.
 */
export interface PreparedBatchGroup extends PreparedEmail {
  transaction_ids: number[];
  reference_numbers: string[];
  /** De-duped attachments, kept grouped by the transaction that contributed them. */
  attachment_groups: BatchAttachmentGroup[];
  /** Language variant this group rendered in, after the conflict policy. */
  language: string;
}

export interface PreparedBatch {
  email_type: string;
  scope: 'batch';
  /** Template display name, for a modal header that can't use the email_type i18n map. */
  template_label: string;
  groups: PreparedBatchGroup[];
  /** Rollup: de-duped union of per-transaction issues + batch-level blockers. */
  classified_issues: import('../template/validation.ts').ClassifiedIssues;
  transaction_issues: BatchTransactionIssues[];
  /** Distinct languages across the selection, first-seen order. */
  languages: string[];
  transaction_count: number;
}

// ── Handlebars helpers (register once) ──────────────────────────────────

const registered = new WeakSet<typeof Handlebars>();
function ensureHelpers() {
  if (registered.has(Handlebars)) return;
  registered.add(Handlebars);

  // Handlebars invokes helpers with whatever the template scope holds, so every
  // parameter below is declared as the type the underlying helper consumes and
  // Handlebars' own `(...args: any[])` delegate signature accepts it.
  Handlebars.registerHelper('formatDate', (v: string | null | undefined) => formatDate(v));
  Handlebars.registerHelper('formatTime', (v: string | null | undefined) => formatTime(v));
  Handlebars.registerHelper('isoWeek', (v: unknown) => {
    const date = v instanceof Date ? v : new Date(v as string);
    if (isNaN(date.getTime())) return '';
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  });
  Handlebars.registerHelper('year', (v: unknown) => {
    const date = v instanceof Date ? v : new Date(v as string);
    return isNaN(date.getTime()) ? '' : date.getFullYear();
  });
  Handlebars.registerHelper('getDayLocative', (v: string | null | undefined, lang: unknown) => getDayLocative(v, typeof lang === 'string' ? lang : 'pl'));
  Handlebars.registerHelper('getDayName', (v: string | null | undefined, lang: unknown) => getDayName(v, typeof lang === 'string' ? lang : 'en'));
  Handlebars.registerHelper('formatDriverDetails', (transport: TransportLike | null | undefined, lang: unknown) => formatDriverDetails(transport, typeof lang === 'string' ? lang as 'pl' | 'en' : 'pl'));
  Handlebars.registerHelper('EMAIL_FOOTER', (user: UserLike) => new Handlebars.SafeString(EMAIL_FOOTER(user)));
  Handlebars.registerHelper('EMAIL_HEADER', () => new Handlebars.SafeString(EMAIL_HEADER()));
  Handlebars.registerHelper('EMAIL_FOOTER_WRAPPER', (user: UserLike) => new Handlebars.SafeString(EMAIL_FOOTER_WRAPPER(user)));
  Handlebars.registerHelper('filterFiles', (files: FileLike[] | null | undefined, docTypes: string[]) => filterFiles(files, docTypes));
  Handlebars.registerHelper('getContactEmails', (company: CompanyLike | null | undefined, purpose: string) => getContactEmails(company, purpose));
  Handlebars.registerHelper('formatNumber', (v: unknown) => formatNumber(v));
  Handlebars.registerHelper('formatWeight', (v: unknown) => formatWeight(v));
  Handlebars.registerHelper('formatPrice', (obj: PriceLike | null | undefined) => formatPrice(obj));
  Handlebars.registerHelper('formatPhone', (v: string | number | null | undefined) => {
    if (!v) return '';
    const display = formatPhone(String(v));
    const digits = String(v).replace(/[^\d+]/g, '');
    const href = digits.startsWith('+') ? digits : `+${digits}`;
    return new Handlebars.SafeString(`<a href="tel:${href}" style="color:#1d67e2;text-decoration:none;">${display}</a>`);
  });
  Handlebars.registerHelper('productName', (...args: unknown[]) => {
    const product = args[0] as ProductLike | null | undefined;
    const lang = args.length > 2 && typeof args[1] === 'string' ? args[1] : 'pl';
    const modifier = args.length > 3 && typeof args[2] === 'string' ? args[2] : undefined;
    return productName(product, lang, { lower: modifier === 'lower' });
  });

  // Batch helpers (sumBy / groupBy / dateRange / …) — array-shaped aggregates a
  // {{#each transactions}} template needs. Registered from the single
  // BATCH_HELPERS registry so this list can never drift from the edge-fn
  // registration in template-pipeline.ts; the drift test asserts both sites.
  // Each helper already tolerates Handlebars' trailing options-hash argument.
  for (const [name, fn] of Object.entries(BATCH_HELPERS)) {
    Handlebars.registerHelper(name, fn as Handlebars.HelperDelegate);
  }
}

// ── Utilities ───────────────────────────────────────────────────────────

const FOOTER_MARKER = '<div dir="ltr" class="gmail_signature"';

function splitEmailFooter(html: string): { messageHtml: string; footerHtml: string } {
  const idx = html.indexOf(FOOTER_MARKER);
  if (idx === -1) return { messageHtml: html, footerHtml: '' };
  return { messageHtml: html.slice(0, idx).trimEnd(), footerHtml: html.slice(idx) };
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

// ── Service ─────────────────────────────────────────────────────────────

export interface SendEmailParams {
  /** Transaction scope (transaction emails). Exactly one of transactionId / invoiceId / transactionIds. */
  transactionId?: number;
  /** Invoice scope (invoice emails, e.g. correction requests). */
  invoiceId?: number;
  /** Batch scope — ONE Gmail message, one `email_sends` row per id sharing a `batch_id`. */
  transactionIds?: number[];
  emailType: string;
  subject: string;
  htmlBody: string;
  recipients: string[];
  cc: string[];
  attachments: number[];
  isDraft: boolean;
}

export { SentEmail };

/** Provider for the current user's identity — injected at construction. */
export type GetCurrentUser = () => Promise<{ id: string; email?: string }> | { id: string; email?: string };

export class EmailService {
  constructor(
    private readonly sb: SupabaseClient,
    private readonly googleClient?: GoogleEdgeFnClient,
    private readonly getCurrentUser?: GetCurrentUser,
  ) {}

  async prepare(transactionId: number, emailType: string, userId?: string, userEmail?: string, transportIds?: number[]): Promise<PreparedEmail> {
    // Resolve user from getCurrentUser if not provided explicitly
    if (!userId && this.getCurrentUser) {
      const user = await this.getCurrentUser();
      userId = user.id;
      userEmail = userEmail ?? user.email;
    }
    ensureHelpers();

    const config = await this.loadConfig(emailType);
    const data = await this.fetchData(config, { transactionId, userId, userEmail, transportId: transportIds?.[0] });
    return this.finalize(config, data, emailType);
  }

  /**
   * Prepare an email from a caller-supplied **context** rather than a single
   * transaction. Used for invoice-scoped emails (e.g. correction requests) whose
   * data is a list assembled by the caller — not a `.single()` row keyed on a
   * transaction id. Runs the exact same render engine as `prepare()`:
   * the config's static / user data_sources still resolve (so `currentUser` is
   * available for the footer), then the caller context is merged on top.
   */
  async prepareWithContext(
    emailType: string,
    context: Record<string, unknown>,
    opts: { userId?: string; userEmail?: string } = {},
  ): Promise<PreparedEmail> {
    let { userId, userEmail } = opts;
    if (!userId && this.getCurrentUser) {
      const user = await this.getCurrentUser();
      userId = user.id;
      userEmail = userEmail ?? user.email;
    }
    ensureHelpers();

    const config = await this.loadConfig(emailType);
    // Only non-transaction data_sources resolve here (transaction-keyed sources
    // see a null param and are skipped); the rest comes from `context`.
    const fetched = await this.fetchData(config, { userId, userEmail } as unknown as PipelineParams);
    // Skip the static placeholder sweep: context templates render lists inside
    // {{#each}} blocks whose loop-scoped vars aren't top-level fields and would
    // otherwise surface as spurious "missing field" warnings. Recipients are
    // still validated (the real send blocker).
    return this.finalize(config, { ...fetched, ...context }, emailType, { sweepPlaceholders: false });
  }

  /**
   * Prepare ONE email whose context is N transactions.
   *
   * The per-transaction half runs the existing pipeline verbatim
   * (`fetchData` → `applyDefaults` → `resolveData`) so a transaction inside a
   * batch is enriched exactly like a transaction sent alone. The batch half then
   * assembles `{ transactions: [...], count, … }`, runs the SAME `resolvers`
   * string over it, and renders once per outgoing email.
   *
   * One `resolvers` string therefore serves both passes. `isBatchScope`
   * discriminates them:
   *
   *   isBatchScope
   *     ? { emailSubject: `Zestawienie ${count} ładunków` }   // batch pass
   *     : { legLabel: `${referenceNumber} — ${loadDateFormatted}` }  // per-tx pass
   *
   * `prepare()` never injects that key, so single-transaction scopes are
   * untouched.
   *
   * Static / unfiltered data sources are fetched ONCE (the `staticDataCache`
   * already de-duplicates them across the N per-transaction fetches) and the
   * transaction-keyed ones are skipped for the batch-level fetch — the same
   * null-param trick `prepareWithContext` uses.
   */
  async prepareBatch(
    transactionIds: number[],
    emailType: string,
    userId?: string,
    userEmail?: string,
  ): Promise<PreparedBatch> {
    if (!Array.isArray(transactionIds) || transactionIds.length === 0) {
      throw new Error('prepareBatch requires at least one transaction id');
    }
    if (transactionIds.length > MAX_BATCH_TRANSACTIONS) {
      throw new Error(
        `Batch too large: ${transactionIds.length} transactions (max ${MAX_BATCH_TRANSACTIONS})`,
      );
    }
    if (!userId && this.getCurrentUser) {
      const user = await this.getCurrentUser();
      userId = user.id;
      userEmail = userEmail ?? user.email;
    }
    ensureHelpers();

    const config = await this.loadConfig(emailType);
    if (config.scope !== 'batch') {
      throw new Error(`Template config is not a batch template: ${emailType}`);
    }

    // Batch-level (non-transaction) sources — currentUser for the footer,
    // dictionaries, anything unfiltered. Transaction-keyed sources see a null
    // param and are skipped.
    const staticScope = await this.fetchData(config, { userId, userEmail } as unknown as PipelineParams);

    // Reference numbers come from a single lookup rather than from the template's
    // data_sources: they drive the modal grouping, the per-transaction issue
    // rollup and the attachment filename prefixes, so they must exist even for a
    // template whose sources don't happen to select them.
    const referenceNumbers = await this.fetchReferenceNumbers(transactionIds);

    const entries: BatchTxEntry[] = [];
    const transactionIssues: BatchTransactionIssues[] = [];

    for (const transactionId of transactionIds) {
      const fetched = await this.fetchData(config, { transactionId, userId, userEmail });
      const enriched = await this.applyDefaults(fetched, config);
      const scope = this.resolveData({ ...enriched, isBatchScope: false, transactionId }, config.resolvers);

      const referenceNumber =
        (scope.referenceNumber as string | undefined)
        ?? referenceNumbers.get(transactionId)
        ?? `TX-${transactionId}`;
      scope.referenceNumber = referenceNumber;

      const recipients = resolveRecipientsFor(config, scope);
      const language = languageFor(config, scope);
      const attachments = selectAttachmentsFor(config, scope);

      // The placeholder sweep runs against the PER-TRANSACTION scope: paths like
      // `referenceNumber` or `unloadPlaceCompany.contacts[…].email` are
      // loop-scoped inside {{#each transactions}} and would read as missing at
      // the batch level. Batch-level names (`count`, `transactions`, …) are the
      // mirror case and are dropped — the batch render proves those resolved.
      const issues = this.validate(config, scope, recipients)
        .filter((path) => path === 'recipients' || !isBatchScopePath(path));
      const classified = classifyBySchema(issues, config.placeholder_schema, scope);

      entries.push({ transactionId, referenceNumber, scope, recipients, language, attachments });
      transactionIssues.push({
        transaction_id: transactionId,
        reference_number: referenceNumber,
        critical: classified.critical,
        info: classified.info,
      });
    }

    const languageDecision = pickBatchLanguage(
      entries.map((e) => e.language),
      batchLanguagePolicy(config),
    );
    const recipientGroups = groupTransactionsByRecipients(
      entries,
      batchRecipientsPolicy(config),
    );
    const currentDate = formatDate(new Date().toISOString());

    const groups: PreparedBatchGroup[] = recipientGroups.map((group) => {
      const groupScope = assembleBatchScope({
        staticScope,
        entries: group.entries,
        language: languageDecision.language,
        currentDate,
      });
      const resolved = this.resolveData(groupScope, config.resolvers);
      const attachments = dedupeAttachments(group.entries.map((e) => ({
        transactionId: e.transactionId,
        referenceNumber: e.referenceNumber,
        files: e.attachments,
      })));
      const email = this.renderEmail(config, resolved, {
        recipientsOverride: group.recipients,
        attachmentsOverride: attachments.flat,
      });

      // Sweep skipped for the same reason `prepareWithContext` skips it — the
      // per-transaction sweep above is the real validation. Recipients stay the
      // one global blocker.
      const validationIssues = email.recipients.length === 0 ? ['recipients'] : [];

      return {
        subject: email.subject,
        html_body: email.htmlBody,
        message_html: email.messageHtml,
        footer_html: email.footerHtml,
        plain_text: email.plainText,
        recipients: email.recipients,
        cc: email.cc,
        attachments: keysToSnakeCase<TransactionFile[]>(email.attachments),
        email_type: emailType,
        validation_issues: validationIssues,
        classified_issues: classifyBySchema(validationIssues, config.placeholder_schema, resolved),
        placeholder_values: {
          reference_numbers: group.entries.map((e) => e.referenceNumber).join(', '),
          transaction_count: group.entries.length,
          language: email.language,
        },
        transaction_ids: group.entries.map((e) => e.transactionId),
        reference_numbers: group.entries.map((e) => e.referenceNumber),
        attachment_groups: attachments.groups.map((g) => ({
          transaction_id: g.transaction_id,
          reference_number: g.reference_number,
          attachments: keysToSnakeCase<TransactionFile[]>(g.files),
        })),
        language: email.language,
      };
    });

    // Rollup: every per-transaction critical, plus the two batch-level blockers.
    const critical = new Set<string>();
    const info = new Set<string>();
    for (const tx of transactionIssues) {
      for (const path of tx.critical) critical.add(path);
      for (const path of tx.info) info.add(path);
    }
    if (groups.some((g) => g.recipients.length === 0)) critical.add('recipients');
    if (languageDecision.blocked) critical.add(BATCH_LANGUAGE_CONFLICT);
    for (const path of critical) info.delete(path);

    return {
      email_type: emailType,
      scope: 'batch',
      template_label: config.name,
      groups,
      classified_issues: { critical: [...critical], info: [...info] },
      transaction_issues: transactionIssues,
      languages: languageDecision.languages,
      transaction_count: transactionIds.length,
    };
  }

  /** Reference numbers (with the `/externalId` suffix applied) for a selection. */
  private async fetchReferenceNumbers(transactionIds: number[]): Promise<Map<number, string>> {
    const map = new Map<number, string>();
    const { data, error } = await this.sb
      .from('transactions')
      .select('id, reference_number, external_id')
      .in('id', transactionIds);
    if (error) throw new Error(`Failed to fetch transactions: ${error.message}`);
    for (const row of (data ?? []) as Array<{ id: number; reference_number: string | null; external_id: string | number | null }>) {
      const base = row.reference_number ?? `TX-${row.id}`;
      map.set(row.id, row.external_id && row.reference_number ? `${base}/${row.external_id}` : base);
    }
    return map;
  }

  /** Shared render core: defaults → resolvers → render → validate → PreparedEmail. */
  private async finalize(
    config: EmailTemplateConfig,
    data: Record<string, unknown>,
    emailType: string,
    opts: { sweepPlaceholders?: boolean } = {},
  ): Promise<PreparedEmail> {
    const enriched = await this.applyDefaults(data, config);
    const resolved = this.resolveData(enriched, config.resolvers);
    const email = this.renderEmail(config, resolved);
    const validationIssues = opts.sweepPlaceholders === false
      ? (email.recipients.length === 0 ? ['recipients'] : [])
      : this.validate(config, resolved, email.recipients);
    const classifiedIssues = classifyBySchema(validationIssues, config.placeholder_schema, resolved);

    return {
      subject: email.subject,
      html_body: email.htmlBody,
      message_html: email.messageHtml,
      footer_html: email.footerHtml,
      plain_text: email.plainText,
      recipients: email.recipients,
      cc: email.cc,
      attachments: keysToSnakeCase<TransactionFile[]>(email.attachments),
      email_type: emailType,
      validation_issues: validationIssues,
      classified_issues: classifiedIssues,
      placeholder_values: {
        reference_number: resolved.referenceNumber,
        product_name: (resolved.product as Record<string, unknown>)?.name || '',
        waste_code: (resolved.product as Record<string, unknown>)?.wasteCode || '',
        load_date: resolved.loadDateFormatted || '',
        unload_date: resolved.unloadDateFormatted || '',
      },
    };
  }

  /** Send email (or save as draft) via Google edge function. Requires googleClient. */
  async send(params: SendEmailParams): Promise<SentEmail> {
    if (!this.googleClient) throw new Error('GoogleEdgeFnClient required for sending emails');
    const sent = await this.googleClient.sendEmail(params);
    await this.claimTransportOrderTransaction(params);
    return sent;
  }

  /**
   * Issue #296 — a sent transport order assigns its sender as the transaction's
   * logistician. Best-effort by design: the mail is already out by the time this
   * runs, so a failure here must never surface as a send failure.
   *
   * A BATCH send carries `transactionIds` instead of `transactionId`, and every
   * transaction in it genuinely received the order — so each one is claimed,
   * exactly as it would be had the operator sent them one by one. Serial (the
   * reconcile sweep's lesson: parallel writes over the same tables deadlock) and
   * guarded per id, so one failing row cannot skip the rest.
   */
  private async claimTransportOrderTransaction(params: SendEmailParams): Promise<void> {
    if (params.isDraft) return;
    if (params.emailType !== 'transport_order') return;
    if (!this.getCurrentUser) return;

    const transactionIds = params.transactionIds?.length
      ? params.transactionIds
      : params.transactionId != null ? [params.transactionId] : [];
    if (transactionIds.length === 0) return;

    let userId: string;
    try {
      const user = await this.getCurrentUser();
      if (!user?.id) return;
      userId = user.id;
    } catch (e) {
      console.error('[EmailService] transport-order auto-assign failed', e);
      return;
    }

    const deps = { employees: new EmployeeDao(this.sb), transactions: new TransactionDao(this.sb) };
    for (const transactionId of transactionIds) {
      try {
        await assignLogisticianOnTransportOrder(deps, transactionId, userId);
      } catch (e) {
        console.error('[EmailService] transport-order auto-assign failed', e);
      }
    }
  }

  /** Prepare locally + send via edge function in one call. */
  async prepareAndSend(
    transactionId: number, emailType: string, userId: string,
    opts: { isDraft?: boolean; userEmail?: string; transportIds?: number[] } = {},
  ): Promise<SentEmail> {
    const prepared = await this.prepare(transactionId, emailType, userId, opts.userEmail, opts.transportIds);
    return this.send({
      transactionId,
      emailType,
      subject: prepared.subject,
      htmlBody: prepared.html_body,
      recipients: prepared.recipients,
      cc: prepared.cc,
      attachments: prepared.attachments.map(a => a.id),
      isDraft: opts.isDraft || false,
    });
  }

  // In-memory cache for template configs (static, changes rarely)
  private configCache = new Map<string, { data: EmailTemplateConfig; ts: number }>();
  private static CONFIG_TTL = 5 * 60 * 1000; // 5 minutes

  private async loadConfig(name: string): Promise<EmailTemplateConfig> {
    const cached = this.configCache.get(name);
    if (cached && Date.now() - cached.ts < EmailService.CONFIG_TTL) return cached.data;

    const { data, error } = await this.sb
      .from('template_configs').select('*').eq('name', name).eq('is_deleted', false).single<EmailTemplateConfig>();
    if (error || !data) throw new Error(`Template config not found: ${name}`);
    if (data.is_disabled) throw new Error(`Template config is disabled: ${name}`);
    const config = data;
    this.configCache.set(name, { data: config, ts: Date.now() });
    return config;
  }

  // Cache for unfiltered data sources (employees, countries — static, no per-row filter)
  private staticDataCache = new Map<string, { data: unknown; ts: number }>();
  private static DATA_TTL = 5 * 60 * 1000; // 5 minutes

  private async fetchData(config: EmailTemplateConfig, params: PipelineParams): Promise<Record<string, unknown>> {
    const results = await Promise.all(
      config.data_sources.map(async (ds) => {
        if (!ds.filter) {
          // Cache unfiltered queries (static tables like employees, countries)
          const cacheKey = `${ds.table}:${ds.select}`;
          const cached = this.staticDataCache.get(cacheKey);
          if (cached && Date.now() - cached.ts < EmailService.DATA_TTL) return { key: ds.key, data: cached.data };

          const { data, error } = await this.sb.from(ds.table).select(ds.select);
          if (error) throw new Error(`Failed to fetch ${ds.table}: ${error.message}`);
          this.staticDataCache.set(cacheKey, { data, ts: Date.now() });
          return { key: ds.key, data };
        }
        const paramValue = (params as unknown as Record<string, unknown>)[ds.filter.param];
        if (paramValue == null) return { key: ds.key, data: null };
        const { data, error } = await this.sb.from(ds.table).select(ds.select).eq(ds.filter.field, paramValue).single();
        if (error) throw new Error(`Failed to fetch ${ds.table}: ${error.message}`);
        return { key: ds.key, data };
      }),
    );

    let merged: Record<string, unknown> = {};
    for (const r of results) {
      if (r.data === null) continue;
      if (r.key) merged[r.key] = r.data;
      else merged = { ...merged, ...(r.data as unknown as Record<string, unknown>) };
    }

    // `referenceNumber` / `externalId` are the only two fields read here; the rest
    // of the row stays opaque and is handed straight to the template scope.
    const camel = keysToCamelCase<Record<string, unknown> & {
      referenceNumber?: string | null;
      externalId?: string | number | null;
    }>(merged);
    if (camel.externalId && camel.referenceNumber) {
      camel.referenceNumber = `${camel.referenceNumber}/${camel.externalId}`;
    }
    return camel;
  }

  private async applyDefaults(data: Record<string, unknown>, config?: EmailTemplateConfig): Promise<Record<string, unknown>> {
    // Fetch countries lookup (for company enrichment and transit resolution)
    let countries: CountryLike[] = [];
    try {
      const { data: rows } = await this.sb.from('countries').select('code, name_en, name_pl, language_code');
      if (rows) countries = keysToCamelCase<CountryLike[]>(rows);
    } catch { /* non-fatal */ }

    const result = { ...data };

    // Formatted dates and times
    result.currentDate = formatDate(new Date().toISOString());
    result.loadDateFormatted = data.loadDate ? formatDate(data.loadDate as string) : null;
    result.unloadDateFormatted = data.unloadDate ? formatDate(data.unloadDate as string) : null;
    result.loadTimeFormatted = data.loadTime ? formatTime(data.loadTime as string) : null;
    result.unloadTimeFormatted = data.unloadTime ? formatTime(data.unloadTime as string) : null;
    result.loadedWeightFormatted = data.loadedWeight != null ? formatWeight(data.loadedWeight as number) : null;
    result.unloadedWeightFormatted = data.unloadedWeight != null ? formatWeight(data.unloadedWeight as number) : null;

    // Transit countries
    result.transitEn = data.transit ? resolveTransit(data.transit as string, countries, 'en') : null;
    result.transitPl = data.transit ? resolveTransit(data.transit as string, countries, 'pl') : null;

    // Enrich company fields
    const companyFields = ['loadPlace', 'unloadPlace', 'exporter', 'importer', 'loadCommercialCompany', 'unloadCommercialCompany'];
    for (const field of companyFields) {
      result[field] = data[field] ? enrichCompany(data[field] as CompanyLike, countries) : null;
    }
    result.loadPlaceCompany = data.loadPlaceCompany ? enrichCompany(data.loadPlaceCompany as CompanyLike, countries) : result.loadPlace;
    result.unloadPlaceCompany = data.unloadPlaceCompany ? enrichCompany(data.unloadPlaceCompany as CompanyLike, countries) : result.unloadPlace;

    // Carriers from transports — sort by sequence_number so Carrier 1 / 2 / 3
    // map to the correct legs on Annex VII (5a) and reload (5b).
    const rawTransports = data.transports as TransportLike[] | undefined;
    const transports = Array.isArray(rawTransports)
      ? [...rawTransports].sort((a, b) => (a?.sequenceNumber ?? 0) - (b?.sequenceNumber ?? 0))
      : undefined;
    result.firstCarrier = transports?.[0] ? enrichCarrier(transports[0], countries) : null;
    result.secondCarrier = transports?.[1] ? enrichCarrier(transports[1], countries) : null;
    result.thirdCarrier = transports?.[2] ? enrichCarrier(transports[2], countries) : null;

    // Transport orders are addressed to each leg's PAYER (commercial company);
    // mirror of template-pipeline.applyDefaults — other doc types keep the carrier.
    if (config?.doc_document_type === 'transport_order' && transports?.length) {
      // Cast: comparing the full supabase-js client against the structural
      // PayerQueryClient blows TS2589 (excessively deep instantiation).
      Object.assign(result, await resolvePayerCarriers(this.sb as unknown as PayerQueryClient, transports, countries));
    }

    return result;
  }

  private resolveData(data: Record<string, unknown>, resolversCode: string | null): Record<string, unknown> {
    if (!resolversCode) return data;
    try {
      const keys = Object.keys(data);
      // `resolversCode` is the `resolvers` column of a `template_configs` row.
      // It is NOT developer-authored: operators edit it through the code editor
      // at /settings/template-configs. RLS on that table
      // (supabase/schema/schema.sql) restricts INSERT/UPDATE to the `admin` and
      // `management` roles, while any employee may SELECT — so this executes
      // admin-authored JS in every employee's browser. That is the intended
      // design of the template-resolver feature, and it is the reason the rule
      // is disabled rather than the construct removed.
      //
      // MIRROR: supabase/functions/google-api-service/template-pipeline.ts
      // runs the identical `new Function` over the same column, server-side in
      // Deno. Any change here must land on both sides.
      //
      // Worth revisiting: a compromised admin account gets JS execution in
      // every employee browser AND in the edge-function runtime. Replacing this
      // with a restricted expression evaluator would remove that amplifier, but
      // it is a behavior change and belongs in its own reviewed unit of work.
      // eslint-disable-next-line @typescript-eslint/no-implied-eval -- admin/management-authored template resolvers, gated by RLS; mirrored in the google-api-service edge function. See the note above.
      const fn = new Function(...keys, 'helpers', `"use strict"; return (${resolversCode});`) as (...args: unknown[]) => unknown;
      const helpers = {
        formatDate, formatTime, formatWeight, formatNumber, formatPhone, formatPrice,
        productName, getDayLocative, getDayName,
        getContactEmails, filterFiles, selectApprovedFile,
        nationalCode, formatTransportMeans,
        // Batch aggregates — a batch resolver computing totals / groupings /
        // sort order reaches them here, exactly as it would in a template.
        ...BATCH_HELPERS,
      };
      const resolved: unknown = fn(...keys.map(k => data[k]), helpers);
      if (resolved && typeof resolved === 'object' && !Array.isArray(resolved)) return { ...data, ...resolved };
      return data;
    } catch (err) {
      console.error('[EmailService] Resolver failed:', err);
      return data;
    }
  }

  /**
   * Render subject + body + recipients + cc + attachments from a resolved scope.
   *
   * Recipient / attachment / language resolution lives in `./batchScope.ts` and
   * is shared with the edge-fn engine AND with the per-transaction pass of
   * `prepareBatch` — so one transaction resolves identically whether it is sent
   * alone or as part of a batch, by construction rather than by convention.
   *
   * `opts` overrides exist for the batch path only: a batch group's recipients
   * come from the recipients POLICY (union / split across N transactions) and
   * its attachments from the de-duped aggregate, neither of which the
   * single-scope rules above can produce. The single-transaction path passes no
   * opts and behaves exactly as before.
   */
  private renderEmail(
    config: EmailTemplateConfig,
    data: Record<string, unknown>,
    opts: { recipientsOverride?: string[]; attachmentsOverride?: FileLike[] } = {},
  ) {
    // Pick language: resolver override → config source (company languageCode) → default (pl)
    const lang = languageFor(config, data);
    const templateStr = config.email_body_templates?.[lang] || config.email_body_template;
    if (!templateStr) throw new Error(`No email body template for: ${config.name}`);

    const htmlBody = Handlebars.compile(templateStr)(data);
    const { messageHtml, footerHtml } = splitEmailFooter(htmlBody);

    let subject = '';
    const subjectStr = config.email_subject_templates?.[lang] || config.email_subject_template;
    if (data.emailSubject) subject = data.emailSubject as string;
    else if (subjectStr) subject = Handlebars.compile(subjectStr)(data);

    const recipients: string[] = opts.recipientsOverride ?? resolveRecipientsFor(config, data);

    const cc: string[] = config.email_cc?.length ? config.email_cc : (data.emailCC as string[] || []);

    const attachments: FileLike[] = opts.attachmentsOverride ?? selectAttachmentsFor(config, data);

    return { subject, htmlBody, messageHtml, footerHtml, plainText: stripHtml(htmlBody), recipients, cc, attachments, language: lang };
  }

  private validate(config: EmailTemplateConfig, data: Record<string, unknown>, recipients: string[]): string[] {
    const issues: string[] = [];
    // Union of placeholders across: body (+language variants), subject
    // (+language variants), identifiers referenced by the resolver, and
    // composite paths like `firstCarrier.contacts[transport_notification].email`.
    const allPaths = extractAllEmailPlaceholders(config);
    for (const path of allPaths) {
      if (!isFieldPopulated(path, data)) issues.push(path);
    }
    if (recipients.length === 0) issues.push('recipients');
    return issues;
  }
}
